import React, {
  useReducer,
  useEffect,
  useRef,
  useContext,
  useState,
  useCallback,
  createContext
} from 'react';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import useTranslations from '../../hooks/useTranslations';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import fetchAPI from '../../utils/fetchAPI';
import generateDuplicationName from '../../utils/generateDuplicationName';
import generateId from '../../utils/generateId';
import log from '../../utils/log';
import Animation from '../Animation';
import FormBuilderAutomationsView from '../view/FormBuilderAutomationsView';
import { generateDeleteSchedulerPromise } from '../automations/engine/AutomationsTriggerEngine';
import AutomationWizardEngine from '../automations/engine/AutomationWizardEngine';
import FormBuilderCustomButtonsEngine from './FormBuilderCustomButtonsEngine';
import { replaceElements } from './FormBuilderStatusesEngine';
import getUrlParam from '../../utils/getUrlParam';
import FormBuilderBulkActionsEngine from './FormBuilderBulkActionsEngine';

export const ACTIVE_VIEW = {
  AUTOMATION_WIZARD: 'aw',
  AUTOMATIONS_LIST: 'al',
  CUSTOM_BUTTONS: 'cb',
  BULK_ACTIONS: 'ba',
  BULK_ACTION_WIZARD: 'bw'
};

const filterAutomations = (automationTitle, filter) => {
  if (!filter) {
    return true;
  }
  return automationTitle.toLowerCase().includes(filter.toLowerCase());
};

const AutomationsReducer = (state, action) => {
  const { type, payload, id, pos } = action;
  let index, automationsCpy;
  switch (type) {
    case 'update':
      return { ...state, ...payload };
    case 'update_single_automation':
      index = state.automations.findIndex(a => a.internalId === id);
      automationsCpy = JSON.parse(JSON.stringify(state.automations));
      automationsCpy[index] = payload;
      return { ...state, automations: automationsCpy };
    default:
      return state;
  }
};

const AutomationsSchedulersReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case 'update':
      return { ...state, ...payload };
    default:
      return state;
  }
};

export const AutomationsContex = createContext({});

const initialReducerState = {
  automations: [],
  admins: [],
  automationInEdit: {}
};

const FormBuilderAutomationsEngine = ({
  form,
  formId,
  updateAutomationsOnRedux = automations => automations
}) => {
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const [data, dispatch] = useReducer(AutomationsReducer, initialReducerState);
  const [schedulers, dispatchSchedulers] = useReducer(
    AutomationsSchedulersReducer,
    {}
  );
  const prevActiveView = useRef(ACTIVE_VIEW.AUTOMATIONS_LIST);
  const [activeView, _setActiveView] = useState(ACTIVE_VIEW.AUTOMATIONS_LIST);
  const { setIsOTLoading } = useGlobalVariables();
  const skipUpdate = useRef(true); // prevent update from triggering API call
  const isFirstRender = useRef(true);
  const showSnackbarOnSave = useRef(false);
  const t = useTranslations();

  const [filter, setFilter] = useState(null);

  const getAutomationForInit = useCallback(() => {
    const found = form.automations.find(
      element => element.internalId === getUrlParam('automationId')
    );
    return found;
  }, [form]);

  const setActiveView = useCallback(
    value => {
      prevActiveView.current = activeView;
      _setActiveView(value);
    },
    [activeView]
  );

  const initData = useCallback(
    (admins, schedulers, { hasWorldcheck, hasThirdfort, hasCredas }) => {
      const mappedSchedulers = {};
      const automationInParam = getUrlParam('automationId')
        ? getAutomationForInit()
        : {};
      schedulers.forEach(s => (mappedSchedulers[s.automationId] = s));
      dispatch({
        type: 'update',
        payload: {
          admins,
          automations: form.automations,
          automationInEdit: automationInParam,
          hasWorldcheck,
          hasThirdfort,
          hasCredas
        }
      });
      getUrlParam('automationId') && openWizard(automationInParam);
      window.history.pushState(
        null,
        null,
        location.href.split('/automations')[0]
      );
      dispatchSchedulers({ type: 'update', payload: { ...mappedSchedulers } });
    },
    [form, getAutomationForInit, location.href]
  );

  useEffect(() => {
    const getFormAdmins = new Promise((resolve, reject) => {
      fetchAPI(
        `/forms/v1/forms/${form.id}/collaborate?limit=500&addUserInfo=true`
      )
        .then(response => resolve(response.results))
        .catch(reject);
    });

    const getSchedulerData = new Promise((resolve, reject) => {
      fetchAPI(
        `/scheduler/v1/schedule?sort='metadata'->'createdDate' asc&query=[[{"field": "'assetId'", "op": "=", "value": "${formId}" }, {"field": "'jobType'", "op": "=", "value": "automations" }]]`
      )
        .then(response => resolve(response.results))
        .catch(reject);
    });

    const getIntegrationsData = new Promise((resolve, reject) => {
      fetchAPI(
        `/scheduler/v1/integration?query=[[{"field":"'assetId'","op":"=","value":"${formId}"}]]`
      )
        .then(res => {
          const worldcheckResults =
            res && res.results
              ? res.results.filter(
                  one => one.integrationType === 'worldcheck' && one.active
                )
              : [];

          const hasWorldcheck = worldcheckResults.length > 0;

          const thirdfortResults =
            res && res.results
              ? res.results.filter(
                  one => one.integrationType === 'thirdfort' && one.active
                )
              : [];

          const hasThirdfort = thirdfortResults.length > 0;

          const credasResults =
            res && res.results
              ? res.results.filter(
                  one => one.integrationType === 'credas' && one.active
                )
              : [];

          const hasCredas = credasResults.length > 0;

          resolve({ hasWorldcheck, hasThirdfort, hasCredas });
        })
        .catch(reject);
    });

    setIsOTLoading(true);
    Promise.all([getFormAdmins, getSchedulerData, getIntegrationsData])
      .then(results => {
        console.log('Results:', results); // Add your console log here
        initData(results[0], results[1], results[2]);
      })
      .catch(log)
      .finally(() => setIsOTLoading(false));
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (skipUpdate.current) {
      skipUpdate.current = false;
      return;
    }

    fetchAPI(`/forms/v1/forms/${formId}`, {
      method: 'PUT',
      body: { ...form, automations: data.automations }
    })
      .then(() => {
        // changes saved
        if (showSnackbarOnSave.current) {
          showSnackbarOnSave.current = false;
          openSnackbar('success', 'automation.snackbar.success.general');
        }

        updateAutomationsOnRedux(data.automations);
      })
      .catch(e => openSnackbar('error', 'snackbars.statuses.save.err'));
  }, [data]);

  const openWizard = useCallback(
    automationData => {
      skipUpdate.current = true;
      dispatch({
        type: 'update',
        payload: { automationInEdit: automationData }
      });
      setActiveView(ACTIVE_VIEW.AUTOMATION_WIZARD);
    },
    [setActiveView]
  );

  const closeWizard = useCallback(
    (automationData, schedulerPromise) => {
      if (!automationData) {
        setActiveView(prevActiveView.current);
        return;
      }

      showSnackbarOnSave.current = true;

      if (
        data.automations.find(a => a.internalId === automationData.internalId)
      ) {
        dispatch({
          type: 'update_single_automation',
          payload: automationData,
          id: automationData.internalId
        });
      } else {
        automationData.position = data.automations.length;
        const automationsCpy = JSON.parse(JSON.stringify(data.automations));
        automationsCpy.push(automationData);
        dispatch({ type: 'update', payload: { automations: automationsCpy } });
      }

      // handle promise properly
      schedulerPromise();
      setTimeout(() => {
        setActiveView(prevActiveView.current);
      }, 100);
    },
    [data, setActiveView]
  );

  const isTitleValid = useCallback(
    (title, pos) => {
      if (!!!title || !!!title.length) {
        return 'empty_title';
      }
      if (
        data.automations.some(
          a => t(a.label.trim()) === t(title.trim()) && a.position !== pos
        )
      ) {
        return 'existing';
      }
      return '';
    },
    [data]
  );

  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return;
      }
      if (result.destination.index === result.source.index) {
        return;
      }
      const previousIndex = result.source.index;
      const newIndex = result.destination.index;
      const sortedList = replaceElements(
        data.automations,
        previousIndex,
        newIndex
      ).map((s, i) => ({ ...s, position: i }));
      dispatch({
        type: 'update',
        payload: { automations: sortedList }
      });
    },
    [data]
  );

  const onAddNewAutomation = useCallback(() => {
    const basicStructure = {
      label: '',
      rule: {
        when: {
          eventType: ''
        },
        actionsList: []
      },
      position: -1,
      internalId: generateId()
    };

    openWizard(basicStructure);
  }, []);

  const onSearch = useCallback(
    searchValue => {
      setFilter(!!searchValue ? searchValue : null);
    },
    [data]
  );
  const onAutomationDelete = useCallback(
    internalId => {
      const allAutomationsCpy = JSON.parse(JSON.stringify(data.automations));
      const automation2DeleteIndex = allAutomationsCpy.findIndex(
        a => a.internalId === internalId
      );

      if (automation2DeleteIndex < 0) {
        // didn't find - very strange
        openSnackbar('error', 'snackbars.general.error');
        return;
      }
      allAutomationsCpy.splice(automation2DeleteIndex, 1);
      allAutomationsCpy.forEach(a => {
        if (a.position >= automation2DeleteIndex) {
          a.position--;
        }
      });

      dispatch({ type: 'update', payload: { automations: allAutomationsCpy } });

      if (schedulers[internalId]) {
        generateDeleteSchedulerPromise(schedulers[internalId].id)();
      }
    },
    [data, schedulers]
  );

  const onAutomationDuplicate = useCallback(
    async internalId => {
      let allPromises, wantedAutomationString;
      const allAutomationsCpy = JSON.parse(JSON.stringify(data.automations));
      const wantedAutomation = allAutomationsCpy.find(
        a => a.internalId === internalId
      );
      const positionAnchor = wantedAutomation.position;
      /**
       * If automation emails have been edited, we need to duplicate the emails too,
       * and use the new ones. Otherwise, editing one email will edit both.
       */
      wantedAutomationString = JSON.stringify(wantedAutomation);
      const regularExpression = /"emailTemplateId":\s*"([^"]+)"/gi;
      const matches = wantedAutomationString.match(regularExpression);
      const allEmailTemplateIds = matches
        ? matches
            .map(match => match.slice(19, -1))
            .filter(tid => !tid.startsWith('00000000'))
        : [];
      allPromises = allEmailTemplateIds.map(tid => {
        return fetchAPI(`/mailer/v1/emails/${tid}/duplicate`, {
          method: 'POST',
          handleResponse: r => {
            return r.headers.map.location;
          }
        }).then(location => {
          const newTemplateId = location
            ? location.replace('mailer/', '')
            : undefined;
          const re = new RegExp(`\\b${tid}\\b`, 'gi');
          if (newTemplateId) {
            wantedAutomationString = wantedAutomationString.replace(
              re,
              newTemplateId
            );
          }
        });
      });
      await Promise.all(allPromises);
      const automationCpy = JSON.parse(wantedAutomationString);

      automationCpy.internalId = generateId();
      automationCpy.label = generateDuplicationName(
        data.automations.map(a => t(a.label)),
        t(automationCpy.label)
      );
      allAutomationsCpy.splice(positionAnchor + 1, 0, automationCpy);
      allAutomationsCpy.forEach(a => {
        if (a.position >= positionAnchor && a.internalId !== internalId) {
          /**
           * We want to push all automations that are located below the duplicated
           * automation down by one. Because the new automation has the same position
           * value as the original auotmation we need to increase it's position as well
           * (but no the original's position index)
           *
           */
          a.position++;
        }
      });
      dispatch({ type: 'update', payload: { automations: allAutomationsCpy } });
      if (schedulers[internalId]) {
        const body = { ...schedulers[internalId] };
        body.id = undefined;
        body.automationId = automationCpy.internalId;
        fetchAPI('/scheduler/v1/schedule', {
          method: 'POST',
          body,
          handleResponse: r => {
            const id = r.headers.map.location;
            body.id = id;
            dispatchSchedulers({
              type: 'update',
              payload: { [automationCpy.internalId]: body }
            });
          }
        }).catch(() => openSnackbar('error', 'snackbars.general.error'));
      }
    },
    [data, schedulers]
  );

  const viewProps = {
    onDragEnd,
    statuses: form.customStatuses,
    automations: data.automations.filter(
      a =>
        a.rule.when.eventType !== 'new' && filterAutomations(t(a.label), filter)
    ),
    staticAutomations: data.automations.filter(
      a => a.rule.when.eventType === 'new'
    ),
    onAddNewAutomation,
    dispatch,
    openWizard,
    onSearch,
    filter,
    onAutomationDelete,
    showSnackbarOnSave,
    setActiveView
  };

  const wizardProps = {
    isTitleValid,
    onWizardClose: closeWizard,
    automationData: data.automationInEdit,
    schedulerData: schedulers[data.automationInEdit.internalId] || {}
  };

  const contextProps = {
    form,
    formAdmins: data.admins || [],
    statuses: form.customStatuses,
    filter,
    hasWorldcheck: data.hasWorldcheck,
    hasThirdfort: data.hasThirdfort,
    hasCredas: data.hasCredas,
    onAutomationDuplicate,
    updateScedulerInState: schedulerData =>
      dispatchSchedulers({
        type: 'update',
        payload: { [schedulerData.automationId]: schedulerData }
      }),
    deleteSchedulerInState: automationId =>
      dispatchSchedulers({
        type: 'update',
        payload: { [automationId]: {} }
      }),
    setActiveView,
    openWizard
  };

  return (
    <AutomationsContex.Provider value={contextProps}>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.AUTOMATIONS_LIST}
      >
        <FormBuilderAutomationsView {...viewProps} />
      </Animation>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.CUSTOM_BUTTONS}
        unmountOnExit
        mountOnEnter
      >
        <FormBuilderCustomButtonsEngine />
      </Animation>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.BULK_ACTIONS}
        unmountOnExit
        mountOnEnter
      >
        <FormBuilderBulkActionsEngine />
      </Animation>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.AUTOMATION_WIZARD}
        unmountOnExit
        mountOnEnter
      >
        <AutomationWizardEngine {...wizardProps} />
      </Animation>
    </AutomationsContex.Provider>
  );
};

export default FormBuilderAutomationsEngine;
